import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { sdk } from "./sdk"

/**
 * Authorize.net settlements (backend lib/settlements): Settled vs Unsettled on the Analytics
 * dashboard, the batch list (Analytics → Settlements), one batch's transactions, Sync now, CSV.
 */
export type SyncStatus = { connected: boolean; environment: "sandbox" | "production"; last_run_at: string | null; last_ok_at: string | null; last_error: string | null; setup_required: boolean; setup_message: string | null }
export type Money = { charges: number; refunds: number; net: number; count: number }
export type Summary = {
  range: { since: string; until: string; timezone: string }
  environment: string
  settled: { terify: Money; other: Money; account: { batches: number; charges: number; refunds: number; net: number } }
  unsettled: { terify: Money; other: Money; authorized: { terify: number; other: number } }
  unsettled_now: { terify: Money; other: Money; authorized: { terify: number; other: number } }
  status: SyncStatus
}
export type Batch = {
  batch_id: string
  settled_at: string
  settled_at_local: string | null
  settled_day: string
  state: string
  payment_method: string | null
  charge_count: number
  charge_amount: number
  refund_count: number
  refund_amount: number
  net: number
  void_count: number
  decline_count: number
  chargeback_count: number
  chargeback_amount: number
  orders_matched: number
  terify_no_order: number
  not_from_terify: number
  terify_net: number
  other_net: number
  complete: boolean
}
export type BatchList = { range: { since: string; until: string; timezone: string }; environment: string; batches: Batch[]; status: SyncStatus }
export type SettlementTxn = {
  trans_id: string
  batch_id: string | null
  kind: "charge" | "refund"
  status: string
  status_label: string
  amount: number
  submitted_at: string | null
  invoice_number: string | null
  card: string | null
  source: "order" | "subscription" | "checkout" | "external"
  from_terify: boolean
  matched_by: string | null
  order_id: string | null
  order_name: string | null
  sub_charge_id: string | null
  ref_trans_id: string | null
  settled_at: string | null
}
export type SyncResult = { ok: boolean; status: "done" | "not_configured" | "setup_required" | "busy" | "failed"; message?: string; batches?: number; batch_transactions?: number; unsettled?: number; matched?: number; orders_stamped?: number }

const P = "/admin/terify/analytics/settlements"
const KEY = ["analytics", "settlements"]

export const useSettlementSummary = (since: string, until: string, refetch: number | false = false) =>
  useQuery({ queryKey: [...KEY, "summary", since, until], queryFn: () => sdk.client.fetch<Summary>(`${P}/summary`, { query: { since, until } }), placeholderData: keepPreviousData, refetchInterval: refetch })
export const useSettlementBatches = (since: string, until: string) =>
  useQuery({ queryKey: [...KEY, "batches", since, until], queryFn: () => sdk.client.fetch<BatchList>(P, { query: { since, until } }), placeholderData: keepPreviousData })
export const useSettlementBatch = (batchId: string | null) =>
  useQuery({ queryKey: [...KEY, "batch", batchId], queryFn: () => sdk.client.fetch<{ batch: Omit<Batch, "settled_day"> | null; transactions: SettlementTxn[] }>(`${P}/batches/${batchId}`), enabled: !!batchId })
export function useSyncSettlements() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (days?: number) => sdk.client.fetch<{ sync: SyncResult }>(`${P}/sync`, { method: "POST", body: days ? { days } : {} }),
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}
export const SETTLEMENTS_EXPORT = `${P}/export`

/** order.metadata.anet_settlement, written by the sync. */
export type SettlementStamp = { status: "settled" | "unsettled" | "authorized" | "voided"; batch_id: string | null; settled_at: string | null; trans_id: string; gateway_status: string; batch_ids?: string[] }
